/**
 * Operation Spearhead — Core Engine
 *
 * Domain classification, territory gain/decay, consistency index,
 * victory conditions, reward checks, and session processing.
 */

import { db, getLocalDateString } from './db';
import type { DBSession, DBExerciseLog } from './db';
import { computeSetVolume } from './volumeTracking';
import type {
  SpearheadDomain,
  SpearheadProgress,
  DomainContribution,
  SectorState,
  SpearheadLeaderboardEntry,
  SpearheadImpact,
} from './types/spearhead';

// ============================================================================
// CONSTANTS
// ============================================================================

const OPERATION_ID = 'spearhead-2026' as const;
const OPERATION_START = '2026-02-24';
const OPERATION_END = '2026-11-30';

/** What counts as a "standard" session per domain — tuned so real sessions feel impactful */
const DOMAIN_NORMALIZERS: Record<SpearheadDomain, number> = {
  alpha: 5_000,    // 5k lbs tonnage (a solid strength session)
  bravo: 3_000,    // 3k meters (a good run/walk)
  charlie: 60,     // 60 bodyweight reps (solid pull-up/push-up work)
  delta: 4_000,    // 4k meter-load-units (a real ruck or loaded carry session)
};

const INITIAL_CONTROL = 25;
const FLOOR_CONTROL = 10;
const CATCH_UP_THRESHOLD = 30;
const MAX_RETROACTIVE_DECAY_DAYS = 3;
const MAX_GAIN_PER_SESSION = 12; // percent

// ============================================================================
// DOMAIN CLASSIFICATION
// ============================================================================

/** Name-based overrides: these keywords force a specific domain regardless of exerciseType */
const NAME_OVERRIDES: { pattern: RegExp; domain: SpearheadDomain }[] = [
  { pattern: /pull[\s-]?up/i, domain: 'charlie' },
  { pattern: /push[\s-]?up/i, domain: 'charlie' },
  { pattern: /plank/i, domain: 'charlie' },
  { pattern: /burpee/i, domain: 'charlie' },
  { pattern: /ruck/i, domain: 'delta' },
  { pattern: /carry/i, domain: 'delta' },
  { pattern: /farmer/i, domain: 'delta' },
  { pattern: /sled/i, domain: 'delta' },
  { pattern: /drag/i, domain: 'delta' },
  { pattern: /prowler/i, domain: 'delta' },
  { pattern: /walk/i, domain: 'bravo' },
  { pattern: /hike/i, domain: 'bravo' },
  { pattern: /stair/i, domain: 'bravo' },
];

/**
 * Classify a single exercise into domain contributions.
 * Returns partial contributions — a session sums across all exercises.
 */
export function classifyExercise(ex: DBExerciseLog): DomainContribution {
  const contrib: DomainContribution = { alpha: 0, bravo: 0, charlie: 0, delta: 0 };
  const name = (ex.exerciseName || '').toLowerCase();

  // Check name-based overrides first
  for (const override of NAME_OVERRIDES) {
    if (override.pattern.test(name)) {
      const value = getExerciseValue(ex, override.domain);
      contrib[override.domain] += value;
      return contrib;
    }
  }

  // Type-based classification
  switch (ex.exerciseType) {
    case 'weight': {
      // ALPHA: tonnage = weight x reps x sets
      let tonnage = 0;
      if (ex.sets) {
        for (const set of ex.sets) {
          if (set.completed && set.weight && set.reps) {
            tonnage += computeSetVolume(set, ex.exerciseName);
          }
        }
      }
      contrib.alpha = tonnage;
      break;
    }

    case 'bodyweight': {
      // CHARLIE: total reps
      let reps = 0;
      if (ex.sets) {
        for (const set of ex.sets) {
          if (set.completed) reps += set.reps || 0;
        }
      }
      if (ex.actualReps) reps = Math.max(reps, ex.actualReps);
      contrib.charlie = reps;
      break;
    }

    case 'timed': {
      // CHARLIE: seconds / 2
      let seconds = 0;
      if (ex.actualTime) {
        seconds = parseTimeToSeconds(ex.actualTime);
      }
      contrib.charlie = seconds / 2;
      break;
    }

    case 'cardio': {
      // BRAVO: distance (meters), or seconds x 2
      if (ex.actualDistance) {
        contrib.bravo = ex.actualDistance;
      } else if (ex.actualTime) {
        contrib.bravo = parseTimeToSeconds(ex.actualTime) * 2;
      }
      break;
    }

    case 'interval': {
      // BRAVO: distance (meters)
      let totalDist = 0;
      if (ex.intervalSets) {
        for (const set of ex.intervalSets) {
          if (set.completed) totalDist += set.distance || 0;
        }
      }
      if (ex.actualDistance) totalDist = Math.max(totalDist, ex.actualDistance);
      contrib.bravo = totalDist;
      break;
    }

    case 'swim-sets': {
      // BRAVO: distance (meters)
      let totalDist = 0;
      if (ex.swimSets) {
        for (const set of ex.swimSets) {
          if (set.completed) totalDist += set.distance || 0;
        }
      }
      contrib.bravo = totalDist;
      break;
    }

    case 'ruck':
    case 'carry': {
      // DELTA: distance x (1 + loadFactor)
      const distance = ex.actualDistance || 0;
      const load = (ex.actualLoad || 0) + (ex.plateCarrierWeight || 0);
      const loadFactor = Math.min(1.0, load / 50); // 50 lbs = full factor
      contrib.delta = distance * (1 + loadFactor);
      break;
    }

    case 'circuit': {
      // 70% CHARLIE / 30% BRAVO: rounds x 50 + time
      let rounds = ex.actualRounds || 0;
      let time = 0;
      if (ex.circuitSets) {
        for (const set of ex.circuitSets) {
          if (set.completed) {
            rounds = Math.max(rounds, set.round);
            if (set.time) time += parseTimeToSeconds(set.time);
          }
        }
      }
      const total = rounds * 50 + time;
      contrib.charlie = total * 0.7;
      contrib.bravo = total * 0.3;
      break;
    }
  }

  return contrib;
}

/**
 * Classify an entire session into domain contributions.
 */
export function classifySession(exercises: DBExerciseLog[]): DomainContribution {
  const total: DomainContribution = { alpha: 0, bravo: 0, charlie: 0, delta: 0 };

  for (const ex of exercises) {
    if (!ex.completed) continue;
    const c = classifyExercise(ex);
    total.alpha += c.alpha;
    total.bravo += c.bravo;
    total.charlie += c.charlie;
    total.delta += c.delta;
  }

  return total;
}

// ============================================================================
// TERRITORY GAIN
// ============================================================================

/**
 * Calculate control gain for a single domain from a session contribution.
 */
export function calculateGain(
  rawContribution: number,
  currentControl: number,
  consistencyIndex: number,
  domain: SpearheadDomain,
  recoveryScore?: number,
): number {
  if (rawContribution <= 0) return 0;

  const normalizer = DOMAIN_NORMALIZERS[domain];
  const normalizedQuality = Math.min(10, rawContribution / normalizer);
  const diminishingFactor = 1 - (currentControl / 120);

  // Base gain: 1.5x multiplier so a solid session moves the needle visibly
  // A "standard" session (normalizedQuality=1) at 25% control yields ~1.2% base gain
  const baseGain = normalizedQuality * 1.5 * diminishingFactor;

  // Early-phase boost: below 40% control, amplify gains to avoid stuck-at-start
  const earlyBoost = currentControl < 40 ? 1.0 + ((40 - currentControl) / 40) * 0.5 : 1.0;

  // Consistency is the WEAPON — high CI = massive gains, low CI = stunted
  // CI=0 → 0.6x, CI=0.5 → 1.05x, CI=1.0 → 1.5x
  const consistencyMultiplier = 0.6 + (consistencyIndex * 0.9);

  // Recovery bonus: good recovery boosts gains, training through bad recovery halves them
  let recoveryMultiplier = 1.0;
  if (recoveryScore !== undefined) {
    if (recoveryScore >= 70) {
      recoveryMultiplier = 1.0 + ((recoveryScore - 70) / 100); // up to +0.3
    } else if (recoveryScore < 50) {
      recoveryMultiplier = 0.5; // training through bad recovery = 50% efficiency
    }
  }

  const gain = baseGain * earlyBoost * consistencyMultiplier * recoveryMultiplier;
  console.log(`[Spearhead] Gain ${domain}: raw=${rawContribution.toFixed(0)} norm=${normalizedQuality.toFixed(2)} base=${baseGain.toFixed(2)} early=${earlyBoost.toFixed(2)} CI=${consistencyMultiplier.toFixed(2)} → +${Math.min(MAX_GAIN_PER_SESSION, gain).toFixed(2)}%`);
  return Math.min(MAX_GAIN_PER_SESSION, gain);
}

// ============================================================================
// TERRITORY DECAY
// ============================================================================

/**
 * Calculate daily decay for a single sector.
 */
export function calculateDailyDecay(
  sectorControl: number,
  consistencyIndex: number,
  daysSinceLastSession: number,
  recoveryScore?: number,
): number {
  // Grace period: 2 days of rest is normal between sessions
  if (daysSinceLastSession <= 2) return 0;

  // EREBUS pushes back — decay reflects real threat
  // At 25% control: ~1.0%/day, At 50%: ~1.2%, At 80%: ~1.4%
  const baseDailyDecay = 0.8 + (sectorControl / 100) * 0.8;

  // High consistency SHIELDS you — earned through prior discipline
  // CI=1.0 → 0.4x decay (very protected), CI=0 → 1.0x (fully exposed)
  const consistencyBuffer = Math.max(0.3, 1.0 - (consistencyIndex * 0.6));

  let recoveryPenalty = 1.0;
  if (recoveryScore !== undefined && recoveryScore < 50) {
    recoveryPenalty = 1.0 + ((50 - recoveryScore) / 100);
  }

  // Accelerating inactivity — each day past grace hits harder
  const inactivityMultiplier = Math.min(2.5, 1.0 + (daysSinceLastSession - 2) * 0.3);

  let totalDecay = baseDailyDecay * consistencyBuffer * recoveryPenalty * inactivityMultiplier;

  // Catch-up: below 30%, decay is halved (EREBUS overextended)
  if (sectorControl < CATCH_UP_THRESHOLD) {
    totalDecay *= 0.5;
  }

  return totalDecay;
}

/**
 * Apply retroactive decay for missed days.
 * Capped at MAX_RETROACTIVE_DECAY_DAYS.
 * Accepts optional breakDecayMultiplier (0.25 = 75% reduction during active break).
 */
export function applyDecay(
  progress: SpearheadProgress,
  today: string,
  daysSinceLastSession: number,
  recoveryScore?: number,
  breakDecayMultiplier?: number,
): SpearheadProgress {
  const lastDecay = progress.lastDecayDate;
  if (lastDecay >= today) return progress; // already decayed today

  const daysSinceDecay = Math.min(
    MAX_RETROACTIVE_DECAY_DAYS,
    daysBetween(lastDecay, today)
  );

  if (daysSinceDecay <= 0) return progress;

  const updated = { ...progress, sectors: { ...progress.sectors } };
  const decayMult = breakDecayMultiplier ?? 1.0;

  for (const domain of ['alpha', 'bravo', 'charlie', 'delta'] as SpearheadDomain[]) {
    const sector = { ...updated.sectors[domain] };

    for (let d = 0; d < daysSinceDecay; d++) {
      let decay = calculateDailyDecay(
        sector.controlPct,
        updated.consistencyIndex,
        daysSinceLastSession,
        recoveryScore,
      );
      // Reduce decay during active training breaks (75% reduction)
      decay *= decayMult;
      sector.controlPct = Math.max(FLOOR_CONTROL, sector.controlPct - decay);
    }

    updated.sectors[domain] = sector;
  }

  updated.lastDecayDate = today;
  return updated;
}

// ============================================================================
// CONSISTENCY INDEX (28-day rolling)
// ============================================================================

/**
 * Calculate consistency index from session history.
 * breakDaysInLast28: number of days in the 28-day window that were during an active
 * training break (excluded from gap penalty calculation).
 */
export function calculateConsistencyIndex(
  consistencyHistory: number[], // last 28 daily session counts
  currentStreakDays: number,
  recoveryEntriesLast7Days: number,
  breakDaysInLast28: number = 0,
): number {
  // Pad to 28 entries
  const history = [...consistencyHistory];
  while (history.length < 28) history.unshift(0);
  const last28 = history.slice(-28);

  // frequencyScore (40%): weeks with 3+ sessions / 4 weeks
  const weeks = [
    last28.slice(0, 7),
    last28.slice(7, 14),
    last28.slice(14, 21),
    last28.slice(21, 28),
  ];
  const weeksWithThreePlus = weeks.filter(w => w.reduce((a, b) => a + b, 0) >= 3).length;
  const frequencyScore = weeksWithThreePlus / 4;

  // streakScore (30%): min(1, currentStreakDays / 14)
  const streakScore = Math.min(1, currentStreakDays / 14);

  // recoveryCompliance (20%): min(1, recoveryEntriesLast7Days / 5)
  const recoveryCompliance = Math.min(1, recoveryEntriesLast7Days / 5);

  // gapPenalty (10%): check for consecutive zero-days
  // Exclude break days from gap counting — treat them as non-existent days
  let maxGap = 0;
  let currentGap = 0;
  const effectiveDays = breakDaysInLast28 > 0
    ? last28.length - Math.min(breakDaysInLast28, last28.length)
    : last28.length;
  // Spread break day exclusions evenly across zero-days (most conservative interpretation)
  let breakExclusionsLeft = breakDaysInLast28;
  for (const count of last28) {
    if (count === 0) {
      if (breakExclusionsLeft > 0) {
        breakExclusionsLeft--;
        // Reset gap when hitting a break day (break days don't count as gaps)
        currentGap = 0;
        continue;
      }
      currentGap++;
      maxGap = Math.max(maxGap, currentGap);
    } else {
      currentGap = 0;
    }
  }
  const gapPenalty = maxGap >= 5 ? 0.0 : maxGap >= 3 ? 0.5 : 1.0;

  const index = (frequencyScore * 0.4) + (streakScore * 0.3) + (recoveryCompliance * 0.2) + (gapPenalty * 0.1);
  return Math.min(1.0, Math.max(0.0, index));
}

// ============================================================================
// VICTORY CONDITIONS
// ============================================================================

export function checkVictoryConditions(progress: SpearheadProgress): {
  allSectorsAbove80: boolean;
  consistencyAbove70: boolean;
  noLargeGaps: boolean;
  victoryAchieved: boolean;
} {
  const allSectorsAbove80 = (['alpha', 'bravo', 'charlie', 'delta'] as SpearheadDomain[])
    .every(d => progress.sectors[d].controlPct >= 80);

  const consistencyAbove70 = progress.consistencyIndex > 0.7;

  // Check for 5+ day gaps in last 28 days
  const history = progress.consistencyHistory.slice(-28);
  let maxGap = 0;
  let currentGap = 0;
  for (const count of history) {
    if (count === 0) {
      currentGap++;
      maxGap = Math.max(maxGap, currentGap);
    } else {
      currentGap = 0;
    }
  }
  const noLargeGaps = maxGap < 5;

  return {
    allSectorsAbove80,
    consistencyAbove70,
    noLargeGaps,
    victoryAchieved: allSectorsAbove80 && consistencyAbove70 && noLargeGaps,
  };
}

// ============================================================================
// REWARD CHECKS
// ============================================================================

const SPEARHEAD_REWARDS = {
  'spearhead-emblem': { condition: 'victory' as const },
  'directorate-slayer': { condition: 'victory' as const },
  'territorial-dominance': { condition: 'victory' as const },
  'force-multiplier': { condition: 'peak90' as const },
  'consistency-protocol': { condition: 'consistency14' as const },
};

/**
 * Check and return newly unlocked rewards.
 */
export function checkRewards(
  progress: SpearheadProgress,
  consecutiveHighConsistencyDays: number,
): string[] {
  const newRewards: string[] = [];
  const { victoryAchieved } = checkVictoryConditions(progress);

  // Victory rewards
  if (victoryAchieved) {
    for (const [id, def] of Object.entries(SPEARHEAD_REWARDS)) {
      if (def.condition === 'victory' && !progress.unlockedRewards.includes(id)) {
        newRewards.push(id);
      }
    }
  }

  // Force Multiplier: any sector reaches 90% peak
  const anyPeak90 = (['alpha', 'bravo', 'charlie', 'delta'] as SpearheadDomain[])
    .some(d => progress.sectors[d].peakControl >= 90);
  if (anyPeak90 && !progress.unlockedRewards.includes('force-multiplier')) {
    newRewards.push('force-multiplier');
  }

  // Consistency Protocol: consistency >= 0.8 for 14+ consecutive days
  if (consecutiveHighConsistencyDays >= 14 && !progress.unlockedRewards.includes('consistency-protocol')) {
    newRewards.push('consistency-protocol');
  }

  return newRewards;
}

// ============================================================================
// LEADERBOARD SCORE
// ============================================================================

export function calculateCompositeScore(progress: SpearheadProgress): number {
  const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
  const avgControl = domains.reduce((sum, d) => sum + progress.sectors[d].controlPct, 0) / 4;
  return avgControl * (0.7 + progress.consistencyIndex * 0.3);
}

// ============================================================================
// INITIALIZATION
// ============================================================================

export function createInitialProgress(userId: string, operationId?: string, deploymentId?: string): SpearheadProgress {
  const now = new Date().toISOString();
  const today = getLocalDateString();
  const opId = operationId || OPERATION_ID;

  const makeSector = (): SectorState => ({
    controlPct: INITIAL_CONTROL,
    lifetimeContribution: 0,
    lastContributionDate: today,
    peakControl: INITIAL_CONTROL,
  });

  return {
    id: deploymentId ? `${deploymentId}-${userId}` : `spearhead-2026-${userId}`,
    userId,
    operationId: opId,
    ...(deploymentId ? { deploymentId } : {}),
    sectors: {
      alpha: makeSector(),
      bravo: makeSector(),
      charlie: makeSector(),
      delta: makeSector(),
    },
    consistencyIndex: 0.5,
    consistencyHistory: new Array(28).fill(0),
    victoryConditions: {
      allSectorsAbove80: false,
      consistencyAbove70: false,
      noLargeGaps: true,
    },
    lastDecayDate: today,
    unlockedRewards: [],
    enrolledAt: now,
    lastComputedAt: now,
    syncStatus: 'local',
    updatedAt: new Date(),
  };
}

// ============================================================================
// SANCTIONED REST DAYS
// ============================================================================

/**
 * Record that the app recommended rest today (ACWR too high, recovery debt, etc.)
 * These days are excluded from decay calculation — the app told you to rest,
 * so it shouldn't count against your territory control.
 */
export async function recordSanctionedRestDay(userId: string): Promise<void> {
  const today = getLocalDateString();
  try {
    const progress = await loadSpearheadProgress(userId);
    if (!progress.sanctionedRestDays) progress.sanctionedRestDays = [];
    if (!progress.sanctionedRestDays.includes(today)) {
      progress.sanctionedRestDays.push(today);
      // Keep last 30 days only
      if (progress.sanctionedRestDays.length > 30) {
        progress.sanctionedRestDays = progress.sanctionedRestDays.slice(-30);
      }
      await saveSpearheadProgress(progress);
      console.log(`[Spearhead] Recorded sanctioned rest day: ${today}`);
    }
  } catch {
    // non-critical
  }
}

/**
 * Check if a date range contains sanctioned rest days.
 */
function countSanctionedRestDaysInRange(
  sanctionedDays: string[] | undefined,
  fromDate: string,
  toDate: string,
): number {
  if (!sanctionedDays || sanctionedDays.length === 0) return 0;
  return sanctionedDays.filter(d => d >= fromDate && d <= toDate).length;
}

// ============================================================================
// LOAD / SAVE
// ============================================================================

export async function loadSpearheadProgress(userId: string, operationId?: string, deploymentId?: string): Promise<SpearheadProgress> {
  const id = deploymentId ? `${deploymentId}-${userId}` : `spearhead-2026-${userId}`;

  const existing = await db.spearheadProgress.get(id);

  // Always consult Firestore and prefer whichever copy reflects the most
  // recent actual SESSION contribution. This self-heals a stale local copy
  // when the user trained on another device — the previous "Dexie hit wins"
  // logic left the web showing days-old territory ("94 days" while the phone
  // was current). We key on max sector lastContributionDate (advances only on
  // a real workout) NOT lastComputedAt (which decay/load bumps every open), so
  // a genuinely newer local/offline session still wins and isn't clobbered.
  const remote = await loadSpearheadFromFirestore(userId, id, deploymentId);
  const stamp = (p: SpearheadProgress | null | undefined): string => {
    if (!p?.sectors) return '';
    let m = '';
    (['alpha', 'bravo', 'charlie', 'delta'] as SpearheadDomain[]).forEach(d => {
      const dt = p.sectors[d]?.lastContributionDate;
      if (dt && dt > m) m = dt;
    });
    return m;
  };

  if (remote && stamp(remote) > stamp(existing)) {
    await db.spearheadProgress.put(remote); // adopt the fresher remote
    return remote;
  }
  if (existing) return existing;
  if (remote) {
    await db.spearheadProgress.put(remote);
    return remote;
  }

  return deploymentId
    ? createInitialProgress(userId, deploymentId, deploymentId)
    : createInitialProgress(userId);
}

export async function saveSpearheadProgress(progress: SpearheadProgress): Promise<void> {
  progress.updatedAt = new Date();
  progress.lastComputedAt = new Date().toISOString();
  progress.syncStatus = 'local';
  await db.spearheadProgress.put(progress);
  // Mirror to Firestore so progress survives a device switch / data clear.
  // Best-effort and non-blocking — Dexie stays the source of truth for the
  // live UI; Firestore is the durable cross-device backup.
  void persistSpearheadToFirestore(progress);
}

/**
 * Firestore doc ref for a user's progress. MUST match lib/dexieFirebaseSync:
 *   - team deployment → deployments/{deploymentId}/progress/{uid}
 *   - global          → users/{uid}/spearheadProgress/{operationId}   ('spearhead-2026')
 * The Dexie key adds a `-{uid}` suffix; the Firestore doc id does NOT — using
 * the Dexie id as the doc id (the old bug) pointed at a doc that never exists,
 * so cross-device load silently fell back to stale local data.
 */
async function spearheadDocRef(userId: string, operationId: string, deploymentId?: string) {
  const { doc } = await import('firebase/firestore');
  const { db: fdb } = await import('./firebase');
  return deploymentId
    ? doc(fdb, 'deployments', deploymentId, 'progress', userId)
    : doc(fdb, 'users', userId, 'spearheadProgress', operationId);
}

/** Write progress to the canonical Firestore path. Non-fatal. */
async function persistSpearheadToFirestore(progress: SpearheadProgress): Promise<void> {
  try {
    if (!progress.userId || progress.userId === 'anonymous') return;
    const { setDoc } = await import('firebase/firestore');
    const ref = await spearheadDocRef(progress.userId, progress.operationId, progress.deploymentId);
    await setDoc(ref, { ...progress, syncStatus: 'synced' }, { merge: true });
    // Reflect the synced state locally without recursing through save().
    await db.spearheadProgress.update(progress.id, { syncStatus: 'synced' });
  } catch (e) {
    console.warn('[spearhead] Firestore persist failed (non-critical):', e);
  }
}

/** Read progress from the canonical Firestore path. Returns null when absent.
 *  `dexieId` is the LOCAL cache key to stamp onto the returned object. */
async function loadSpearheadFromFirestore(userId: string, dexieId: string, deploymentId?: string): Promise<SpearheadProgress | null> {
  try {
    if (!userId || userId === 'anonymous') return null;
    const { getDoc } = await import('firebase/firestore');
    const ref = await spearheadDocRef(userId, 'spearhead-2026', deploymentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data() as any;
    // Firestore returns Timestamps for Date fields — convert back.
    if (data.updatedAt && typeof data.updatedAt.toDate === 'function') {
      data.updatedAt = data.updatedAt.toDate();
    }
    // Keep the LOCAL Dexie id for cache consistency (Firestore doc id omits it).
    return { ...data, id: dexieId, userId, syncStatus: 'synced' } as SpearheadProgress;
  } catch (e) {
    console.warn('[spearhead] Firestore load failed (non-critical):', e);
    return null;
  }
}

// ============================================================================
// MAIN SESSION PROCESSOR
// ============================================================================

/** Minimal session shape needed by the Spearhead engine */
interface SpearheadSessionInput {
  id?: string;
  exercises?: DBExerciseLog[];
  readiness?: number;
}

/**
 * Process a completed session for Operation Spearhead.
 * Called as a non-blocking hook after session save.
 * Accepts any object with exercises and readiness (loose coupling with sessionStorage types).
 */
export async function processSessionForSpearhead(
  userId: string,
  session: SpearheadSessionInput,
): Promise<SpearheadImpact | null> {
  // Honor feature flag — Spearhead disabled during ARG ramp-up
  if (!SPEARHEAD_ENABLED) return null;

  // Only process during operation window
  const today = getLocalDateString();
  if (today < OPERATION_START || today > OPERATION_END) return null;

  // Only process sessions with exercises (range/no-exercise sessions skipped)
  if (!session.exercises || session.exercises.length === 0) {
    console.log('[Spearhead] Skipping session — no exercises (range/mobility/rest session)');
    return null;
  }

  // Load or create progress
  let progress = await loadSpearheadProgress(userId);

  // Idempotency guard: skip if this session was already processed
  if (session.id) {
    const processed = progress.processedSessionIds || [];
    if (processed.includes(session.id)) {
      console.log('[Spearhead] Session already processed, skipping:', session.id);
      return null;
    }
  }

  // 1. Apply decay for missed days (reduced during active training breaks)
  const rawDaysSinceLastSession = daysBetween(progress.lastDecayDate, today);
  const recoveryScore = session.readiness ?? undefined;

  // Subtract sanctioned rest days (app told user to rest — shouldn't count as inactivity)
  const sanctionedInRange = countSanctionedRestDaysInRange(
    progress.sanctionedRestDays,
    progress.lastDecayDate,
    today,
  );
  const daysSinceLastSession = Math.max(0, rawDaysSinceLastSession - sanctionedInRange);
  if (sanctionedInRange > 0) {
    console.log(`[Spearhead] ${sanctionedInRange} sanctioned rest day(s) excluded from decay calculation`);
  }

  // Damp decay when a sanctioned break covered the gap being decayed.
  //
  // This asked `isOnActiveBreak()` — live — but decay is applied on the first
  // session BACK, by which point the user has already tapped "End Trip" and
  // the break is archived. So the whole travel gap decayed at full rate,
  // exactly the scenario the damping exists for. Ask about the window instead.
  let breakDecayMultiplier: number | undefined;
  try {
    const { wasOnBreakBetween } = await import('./trainingBreak');
    if (await wasOnBreakBetween(userId, progress.lastDecayDate, today)) {
      breakDecayMultiplier = 0.25; // 75% reduction across a sanctioned break
    }
  } catch {
    // trainingBreak module not available
  }

  progress = applyDecay(progress, today, daysSinceLastSession, recoveryScore, breakDecayMultiplier);

  // 2. Classify session into domain contributions
  const contributions = classifySession(session.exercises);

  // 3. Update consistency history
  const historyIdx = progress.consistencyHistory.length - 1;
  const lastContribDate = progress.sectors.alpha.lastContributionDate;
  if (lastContribDate === today) {
    // Already contributed today, increment
    progress.consistencyHistory[historyIdx] = (progress.consistencyHistory[historyIdx] || 0) + 1;
  } else {
    // New day: advance history
    const daysSinceContrib = daysBetween(lastContribDate, today);
    for (let i = 0; i < Math.min(daysSinceContrib, 28); i++) {
      progress.consistencyHistory.push(i === daysSinceContrib - 1 ? 1 : 0);
    }
    // Trim to 28 entries
    while (progress.consistencyHistory.length > 28) {
      progress.consistencyHistory.shift();
    }
  }

  // 4. Recalculate consistency index
  const streakDays = await getStreakDays(userId);
  const recoveryEntries = await getRecentRecoveryCount(userId, 7);

  // Count break days in last 28 days for gap exclusion
  let breakDaysInLast28 = 0;
  try {
    const { getActiveBreak } = await import('./trainingBreak');
    const activeBreak = await getActiveBreak(userId);
    if (activeBreak) {
      const breakStart = new Date(activeBreak.startDate + 'T00:00:00');
      const twentyEightDaysAgo = new Date();
      twentyEightDaysAgo.setDate(twentyEightDaysAgo.getDate() - 28);
      const effectiveStart = breakStart > twentyEightDaysAgo ? breakStart : twentyEightDaysAgo;
      breakDaysInLast28 = daysBetween(
        effectiveStart.toISOString().split('T')[0],
        today
      );
    }
  } catch {
    // Non-critical
  }

  progress.consistencyIndex = calculateConsistencyIndex(
    progress.consistencyHistory,
    streakDays,
    recoveryEntries,
    breakDaysInLast28,
  );

  // 5. Calculate primary gains per domain, then apply cross-domain bleed
  const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
  console.log(`[Spearhead] Processing session | CI=${progress.consistencyIndex.toFixed(2)} | contributions: A=${contributions.alpha.toFixed(0)} B=${contributions.bravo.toFixed(0)} C=${contributions.charlie.toFixed(0)} D=${contributions.delta.toFixed(0)}`);

  // Snapshot control before gains for impact reporting
  const prevControl: Record<SpearheadDomain, number> = {
    alpha: progress.sectors.alpha.controlPct,
    bravo: progress.sectors.bravo.controlPct,
    charlie: progress.sectors.charlie.controlPct,
    delta: progress.sectors.delta.controlPct,
  };

  // First pass: calculate primary gains
  const primaryGains: Record<SpearheadDomain, number> = { alpha: 0, bravo: 0, charlie: 0, delta: 0 };
  for (const domain of domains) {
    if (contributions[domain] > 0) {
      primaryGains[domain] = calculateGain(contributions[domain], progress.sectors[domain].controlPct, progress.consistencyIndex, domain, recoveryScore);
    }
  }

  // Cross-domain bleed: 15% of each domain's GAIN bleeds into the other 3
  // This represents compound benefits — a heavy deadlift also trains grip (charlie) and movement pattern (delta)
  const BLEED_FACTOR = 0.15;
  const totalGains: Record<SpearheadDomain, number> = { ...primaryGains };
  for (const source of domains) {
    if (primaryGains[source] > 0) {
      const bleedPerDomain = primaryGains[source] * BLEED_FACTOR / 3;
      for (const target of domains) {
        if (target !== source) totalGains[target] += bleedPerDomain;
      }
    }
  }

  // Apply gains
  for (const domain of domains) {
    const gainAmt = totalGains[domain];
    if (gainAmt <= 0) continue;

    const sector = { ...progress.sectors[domain] };
    const prevControl = sector.controlPct;

    sector.controlPct = Math.min(100, sector.controlPct + gainAmt);
    sector.lifetimeContribution += contributions[domain];
    sector.lastContributionDate = today;
    sector.peakControl = Math.max(sector.peakControl, sector.controlPct);

    progress.sectors[domain] = sector;
    console.log(`[Spearhead] ${domain}: ${prevControl.toFixed(1)}% → ${sector.controlPct.toFixed(1)}% (+${gainAmt.toFixed(2)}%)`);
  }

  // 6. Check victory conditions
  const victory = checkVictoryConditions(progress);
  progress.victoryConditions = {
    allSectorsAbove80: victory.allSectorsAbove80,
    consistencyAbove70: victory.consistencyAbove70,
    noLargeGaps: victory.noLargeGaps,
  };

  const victoryJustAchieved = victory.victoryAchieved && !progress.victoryAchievedAt;
  if (victoryJustAchieved) {
    progress.victoryAchievedAt = new Date().toISOString();

    // Award victory rewards through progression system
    try {
      const { loadProgressionState, saveProgressionState } = await import('./progressionService');
      const state = await loadProgressionState(userId);
      const awardKey = 'operation-spearhead-2026-complete';

      if (!state.milestonesUnlocked?.includes(awardKey)) {
        if (!state.milestonesUnlocked) state.milestonesUnlocked = [];
        state.milestonesUnlocked.push(awardKey);

        // Unlock cosmetics
        if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
        if (!state.unlockedEmblems.includes('spearhead-emblem')) {
          state.unlockedEmblems.push('spearhead-emblem');
        }
        if (!state.unlockedTitles) state.unlockedTitles = ['Recruit'];
        if (!state.unlockedTitles.includes('Erebus Hunter')) {
          state.unlockedTitles.push('Erebus Hunter');
        }
        if (!state.unlockedCallingCards) state.unlockedCallingCards = ['default'];
        if (!state.unlockedCallingCards.includes('territorial-dominance')) {
          state.unlockedCallingCards.push('territorial-dominance');
        }

        await saveProgressionState(state);
      }
    } catch (e) {
      console.warn('Spearhead victory reward save failed (non-critical):', e);
    }
  }

  // 7. Check milestone rewards
  const consecutiveHighDays = countConsecutiveHighConsistencyDays(progress);
  const newRewards = checkRewards(progress, consecutiveHighDays);
  if (newRewards.length > 0) {
    progress.unlockedRewards = [...progress.unlockedRewards, ...newRewards];

    // Notify reward unlocks
    for (const reward of newRewards) {
      const name = reward.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      import('./notifications').then(({ notifyRewardUnlocked }) => notifyRewardUnlocked(name)).catch(() => {});
    }

    // Award milestone cosmetics
    try {
      const { loadProgressionState, saveProgressionState } = await import('./progressionService');
      const state = await loadProgressionState(userId);
      let changed = false;

      if (newRewards.includes('force-multiplier')) {
        if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
        if (!state.unlockedEmblems.includes('force-multiplier')) {
          state.unlockedEmblems.push('force-multiplier');
          changed = true;
        }
      }
      if (newRewards.includes('consistency-protocol')) {
        if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
        if (!state.unlockedEmblems.includes('consistency-protocol')) {
          state.unlockedEmblems.push('consistency-protocol');
          changed = true;
        }
      }

      if (changed) await saveProgressionState(state);
    } catch (e) {
      console.warn('Spearhead reward cosmetic save failed (non-critical):', e);
    }
  }

  // 8. Record processed session ID (keep last 100 to avoid unbounded growth)
  if (session.id) {
    if (!progress.processedSessionIds) progress.processedSessionIds = [];
    progress.processedSessionIds.push(session.id);
    if (progress.processedSessionIds.length > 100) {
      progress.processedSessionIds = progress.processedSessionIds.slice(-100);
    }
  }

  // 9. Save progress
  await saveSpearheadProgress(progress);

  // 10. Update leaderboard (non-blocking)
  try {
    await updateLeaderboard(userId, progress);
  } catch (e) {
    console.warn('Spearhead leaderboard update failed (non-critical):', e);
  }

  // 11. Process for team deployments (non-blocking)
  try {
    await processSessionForTeamDeployments(userId, session, contributions);
  } catch (e) {
    console.warn('Team deployment processing failed (non-critical):', e);
  }

  // 12. Return impact data for UI feedback
  const newControl: Record<SpearheadDomain, number> = {
    alpha: progress.sectors.alpha.controlPct,
    bravo: progress.sectors.bravo.controlPct,
    charlie: progress.sectors.charlie.controlPct,
    delta: progress.sectors.delta.controlPct,
  };

  return {
    contributions,
    gains: totalGains,
    prevControl,
    newControl,
    newRewards,
    victoryAchieved: !!victoryJustAchieved,
  };
}

// ============================================================================
// TEAM DEPLOYMENT PROCESSING
// ============================================================================

/**
 * Process a session's contributions for all active team deployments the user belongs to.
 * Reuses the already-classified contributions from global processing.
 */
async function processSessionForTeamDeployments(
  userId: string,
  session: SpearheadSessionInput,
  contributions: DomainContribution,
): Promise<void> {
  let deployments: any[];
  try {
    const { getMyDeployments } = await import('./teamDeployments');
    deployments = await getMyDeployments(userId);
  } catch {
    return; // teamDeployments module not available
  }

  const today = getLocalDateString();
  const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];

  for (const deployment of deployments) {
    if (deployment.status !== 'active') continue;
    if (today < deployment.startDate || today > deployment.endDate) continue;

    try {
      // Load or create team-specific progress
      let progress = await loadSpearheadProgress(userId, deployment.id, deployment.id);

      // Idempotency
      if (session.id) {
        const processed = progress.processedSessionIds || [];
        if (processed.includes(session.id)) continue;
      }

      // Apply decay
      const rawDays = daysBetween(progress.lastDecayDate, today);
      const sanctioned = countSanctionedRestDaysInRange(
        progress.sanctionedRestDays, progress.lastDecayDate, today
      );
      const effectiveDays = Math.max(0, rawDays - sanctioned);
      progress = applyDecay(progress, today, effectiveDays, session.readiness ?? undefined);

      // Update consistency history
      const historyIdx = progress.consistencyHistory.length - 1;
      const lastContribDate = progress.sectors.alpha.lastContributionDate;
      if (lastContribDate === today) {
        progress.consistencyHistory[historyIdx] = (progress.consistencyHistory[historyIdx] || 0) + 1;
      } else {
        const daysSinceContrib = daysBetween(lastContribDate, today);
        for (let i = 0; i < Math.min(daysSinceContrib, 28); i++) {
          progress.consistencyHistory.push(i === daysSinceContrib - 1 ? 1 : 0);
        }
        while (progress.consistencyHistory.length > 28) {
          progress.consistencyHistory.shift();
        }
      }

      // Recalculate consistency
      const streakDays = await getStreakDays(userId);
      const recoveryEntries = await getRecentRecoveryCount(userId, 7);
      progress.consistencyIndex = calculateConsistencyIndex(
        progress.consistencyHistory, streakDays, recoveryEntries, 0
      );

      // Apply gains
      for (const domain of domains) {
        const raw = contributions[domain];
        if (raw <= 0) continue;
        const sector = { ...progress.sectors[domain] };
        const gain = calculateGain(raw, sector.controlPct, progress.consistencyIndex, domain, session.readiness ?? undefined);
        sector.controlPct = Math.min(100, sector.controlPct + gain);
        sector.lifetimeContribution += raw;
        sector.lastContributionDate = today;
        sector.peakControl = Math.max(sector.peakControl, sector.controlPct);
        progress.sectors[domain] = sector;
      }

      // Victory conditions
      const victory = checkVictoryConditions(progress);
      progress.victoryConditions = {
        allSectorsAbove80: victory.allSectorsAbove80,
        consistencyAbove70: victory.consistencyAbove70,
        noLargeGaps: victory.noLargeGaps,
      };
      if (victory.victoryAchieved && !progress.victoryAchievedAt) {
        progress.victoryAchievedAt = new Date().toISOString();
      }

      // Record processed session ID
      if (session.id) {
        if (!progress.processedSessionIds) progress.processedSessionIds = [];
        progress.processedSessionIds.push(session.id);
        if (progress.processedSessionIds.length > 100) {
          progress.processedSessionIds = progress.processedSessionIds.slice(-100);
        }
      }

      await saveSpearheadProgress(progress);

      // Update team leaderboard
      try {
        await updateTeamLeaderboard(userId, progress, deployment.id);
      } catch (e) {
        console.warn(`Team leaderboard update failed for ${deployment.id}:`, e);
      }

      console.log(`[Spearhead] Processed session for team deployment: ${deployment.name}`);
    } catch (e) {
      console.warn(`Failed to process session for deployment ${deployment.id}:`, e);
    }
  }
}

/**
 * Update leaderboard for a team deployment.
 */
async function updateTeamLeaderboard(userId: string, progress: SpearheadProgress, deploymentId: string): Promise<void> {
  try {
    const { doc: firestoreDoc, setDoc: firestoreSetDoc, serverTimestamp: ts } = await import('firebase/firestore');
    const { db: fs } = await import('./firebase');

    const profile = await db.userProfiles.get(userId);
    const displayName = profile?.displayName || 'Operator';
    const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
    const avgControl = domains.reduce((sum, d) => sum + progress.sectors[d].controlPct, 0) / 4;

    const entry: Record<string, any> = {
      userId,
      displayName,
      compositeScore: calculateCompositeScore(progress),
      avgSectorControl: avgControl,
      consistencyIndex: progress.consistencyIndex,
      sectorControl: {
        alpha: progress.sectors.alpha.controlPct,
        bravo: progress.sectors.bravo.controlPct,
        charlie: progress.sectors.charlie.controlPct,
        delta: progress.sectors.delta.controlPct,
      },
      victoryAchieved: !!progress.victoryAchievedAt,
      updatedAt: ts(),
    };
    if (progress.victoryAchievedAt) entry.victoryDate = progress.victoryAchievedAt;

    const ref = firestoreDoc(fs, 'deployments', deploymentId, 'leaderboard', userId);
    await firestoreSetDoc(ref, entry, { merge: true });
  } catch (e) {
    console.warn('Team leaderboard write failed:', e);
  }
}

// ============================================================================
// LEADERBOARD SYNC
// ============================================================================

async function updateLeaderboard(userId: string, progress: SpearheadProgress): Promise<void> {
  try {
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { db: firestore } = await import('./firebase');

    const profile = await db.userProfiles.get(userId);
    // Try multiple sources for display name: callsign > displayName > Firebase Auth > fallback
    let displayName = (profile as any)?.callsign || profile?.displayName || '';
    if (!displayName) {
      try {
        const { getAuth } = await import('firebase/auth');
        const currentUser = getAuth().currentUser;
        displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Operator';
      } catch {
        displayName = 'Operator';
      }
    }

    const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
    const avgControl = domains.reduce((sum, d) => sum + progress.sectors[d].controlPct, 0) / 4;

    // Load user cosmetics for leaderboard display (defaults for users without customization)
    let avatarUrl = '/avatars/private.png';
    let titleName = 'Recruit';
    let rankName = 'Private';
    let emblemUrl = '';
    let callingCardUrl = '';
    try {
      const gamification = await db.progressionStates.get(userId);
      if (gamification) {
        const { AVATARS, TITLES, EMBLEMS, CALLING_CARDS } = await import('./cosmetics');
        const avatarId = gamification.equippedAvatar;
        const titleId = gamification.equippedTitle;
        const emblemId = gamification.equippedEmblem;
        const callingCardId = gamification.equippedCallingCard;
        if (avatarId && AVATARS[avatarId]) {
          avatarUrl = AVATARS[avatarId].image || avatarUrl;
        }
        if (titleId && TITLES[titleId]) {
          titleName = TITLES[titleId].name || titleName;
        }
        if (emblemId && EMBLEMS[emblemId]) {
          emblemUrl = (EMBLEMS[emblemId] as any).icon || (EMBLEMS[emblemId] as any).image || '';
        }
        if (callingCardId && CALLING_CARDS[callingCardId]) {
          callingCardUrl = (CALLING_CARDS[callingCardId] as any).image || '';
        }
        rankName = gamification.rank?.title || rankName;
      }
    } catch { /* non-critical — cosmetics are optional */ }

    const entry: Record<string, any> = {
      userId,
      displayName,
      compositeScore: calculateCompositeScore(progress),
      avgSectorControl: avgControl,
      consistencyIndex: progress.consistencyIndex,
      sectorControl: {
        alpha: progress.sectors.alpha.controlPct,
        bravo: progress.sectors.bravo.controlPct,
        charlie: progress.sectors.charlie.controlPct,
        delta: progress.sectors.delta.controlPct,
      },
      victoryAchieved: !!progress.victoryAchievedAt,
      // Cosmetics for leaderboard display
      avatarUrl,
      titleName,
      rankName,
      emblemUrl,
      callingCardUrl,
      updatedAt: serverTimestamp(),
    };
    if (progress.victoryAchievedAt) entry.victoryDate = progress.victoryAchievedAt;

    const ref = doc(firestore, 'operations', 'spearhead-2026', 'leaderboard', userId);
    await setDoc(ref, entry, { merge: true });
  } catch (e) {
    // Silently fail — leaderboard is non-critical
    console.warn('Leaderboard write failed:', e);
  }
}

// ============================================================================
// HELPERS
// ============================================================================

function parseTimeToSeconds(time: string): number {
  if (!time) return 0;
  const parts = time.split(':').map(Number);
  if (parts.length === 2) return (parts[0] || 0) * 60 + (parts[1] || 0);
  if (parts.length === 3) return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
  return Number(time) || 0;
}

function daysBetween(dateA: string, dateB: string): number {
  const a = new Date(dateA + 'T00:00:00');
  const b = new Date(dateB + 'T00:00:00');
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)));
}

function getExerciseValue(ex: DBExerciseLog, domain: SpearheadDomain): number {
  switch (domain) {
    case 'alpha': {
      let tonnage = 0;
      if (ex.sets) {
        for (const set of ex.sets) {
          if (set.completed && set.weight && set.reps) tonnage += computeSetVolume(set, ex.exerciseName);
        }
      }
      return tonnage;
    }
    case 'charlie': {
      let reps = 0;
      if (ex.sets) {
        for (const set of ex.sets) {
          if (set.completed) reps += set.reps || 0;
        }
      }
      if (ex.actualReps) reps = Math.max(reps, ex.actualReps);
      if (ex.actualTime) reps = Math.max(reps, parseTimeToSeconds(ex.actualTime) / 2);
      return reps;
    }
    case 'bravo': {
      if (ex.actualDistance) return ex.actualDistance;
      if (ex.actualTime) return parseTimeToSeconds(ex.actualTime) * 2;
      return 0;
    }
    case 'delta': {
      const dist = ex.actualDistance || 0;
      const load = (ex.actualLoad || 0) + (ex.plateCarrierWeight || 0);
      if (dist > 0) {
        return dist * (1 + Math.min(1.0, load / 50));
      }
      // Fallback for carries/sleds logged as weight exercises (no distance tracked)
      // Use tonnage as a proxy: weight × reps gives meter-load-unit equivalent
      let tonnage = 0;
      if (ex.sets) {
        for (const set of ex.sets) {
          if (set.completed && set.weight && set.reps) tonnage += computeSetVolume(set, ex.exerciseName);
        }
      }
      // Also check actualLoad × actualReps for single-entry carries
      if (tonnage === 0 && load > 0) {
        const reps = ex.actualReps || 1;
        tonnage = load * reps;
      }
      return tonnage;
    }
  }
}

async function getStreakDays(userId: string): Promise<number> {
  try {
    const state = await db.progressionStates.get(userId);
    return state?.streakDays || 0;
  } catch {
    return 0;
  }
}

async function getRecentRecoveryCount(userId: string, days: number): Promise<number> {
  try {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceStr = since.toISOString().split('T')[0];
    // Use [userId+date] compound index for efficient range query
    const entries = await db.recovery
      .where('[userId+date]')
      .between([userId, sinceStr], [userId, '\uffff'])
      .count();
    return entries;
  } catch {
    return 0;
  }
}

function countConsecutiveHighConsistencyDays(progress: SpearheadProgress): number {
  // Count consecutive days from the end of history where daily CI would be >= 0.8
  // Simplified: count consecutive non-zero days from end of history
  const history = progress.consistencyHistory.slice(-28);
  let count = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i] > 0) count++;
    else break;
  }
  // Only valid if overall CI is >= 0.8
  return progress.consistencyIndex >= 0.8 ? count : 0;
}

// ============================================================================
// REPROCESSING (one-time recalculation with updated formulas)
// ============================================================================

/**
 * Reprocess all sessions for a user during the operation window.
 * Used after formula changes to recalculate territory control from scratch.
 */
export async function reprocessAllSessions(userId: string): Promise<{ sessionsProcessed: number; finalControl: Record<SpearheadDomain, number> }> {
  const today = getLocalDateString();
  if (today < OPERATION_START || today > OPERATION_END) {
    return { sessionsProcessed: 0, finalControl: { alpha: 0, bravo: 0, charlie: 0, delta: 0 } };
  }

  // Get all sessions during operation window
  const sessions = await db.sessions
    .where('userId')
    .equals(userId)
    .toArray();

  const operationSessions = sessions
    .filter(s => {
      const date = new Date(s.date).toISOString().split('T')[0];
      return date >= OPERATION_START && date <= OPERATION_END && s.exercises && s.exercises.length > 0;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Reset progress to initial state
  let progress = createInitialProgress(userId);
  // Preserve sanctioned rest days and rewards
  const existing = await loadSpearheadProgress(userId);
  progress.sanctionedRestDays = existing.sanctionedRestDays;
  progress.unlockedRewards = existing.unlockedRewards;

  // Reprocess each session
  for (const session of operationSessions) {
    const sessionDate = new Date(session.date).toISOString().split('T')[0];

    // Apply decay between sessions
    const daysSinceDecay = daysBetween(progress.lastDecayDate, sessionDate);
    const sanctionedInRange = countSanctionedRestDaysInRange(
      progress.sanctionedRestDays, progress.lastDecayDate, sessionDate
    );
    const effectiveDays = Math.max(0, daysSinceDecay - sanctionedInRange);
    progress = applyDecay(progress, sessionDate, effectiveDays, undefined, undefined);

    // Classify and apply gains
    const contributions = classifySession(session.exercises || []);
    const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];

    // Update consistency history
    const lastContribDate = progress.sectors.alpha.lastContributionDate;
    const historyIdx = progress.consistencyHistory.length - 1;
    if (lastContribDate === sessionDate) {
      progress.consistencyHistory[historyIdx] = (progress.consistencyHistory[historyIdx] || 0) + 1;
    } else {
      const daysSinceContrib = daysBetween(lastContribDate, sessionDate);
      for (let i = 0; i < Math.min(daysSinceContrib, 28); i++) {
        progress.consistencyHistory.push(i === daysSinceContrib - 1 ? 1 : 0);
      }
      while (progress.consistencyHistory.length > 28) {
        progress.consistencyHistory.shift();
      }
    }

    // Recalculate consistency
    const streakDays = await getStreakDays(userId);
    const recoveryEntries = await getRecentRecoveryCount(userId, 7);
    progress.consistencyIndex = calculateConsistencyIndex(
      progress.consistencyHistory, streakDays, recoveryEntries, 0
    );

    // Calculate primary gains with cross-domain bleed (same as processSessionForSpearhead)
    const primaryGains: Record<SpearheadDomain, number> = { alpha: 0, bravo: 0, charlie: 0, delta: 0 };
    for (const domain of domains) {
      if (contributions[domain] > 0) {
        primaryGains[domain] = calculateGain(contributions[domain], progress.sectors[domain].controlPct, progress.consistencyIndex, domain);
      }
    }
    const BLEED = 0.15;
    const totalGains: Record<SpearheadDomain, number> = { ...primaryGains };
    for (const source of domains) {
      if (primaryGains[source] > 0) {
        const bleedPer = primaryGains[source] * BLEED / 3;
        for (const target of domains) {
          if (target !== source) totalGains[target] += bleedPer;
        }
      }
    }

    for (const domain of domains) {
      if (totalGains[domain] <= 0) continue;
      const sector = { ...progress.sectors[domain] };
      sector.controlPct = Math.min(100, sector.controlPct + totalGains[domain]);
      sector.lifetimeContribution += contributions[domain];
      sector.lastContributionDate = sessionDate;
      sector.peakControl = Math.max(sector.peakControl, sector.controlPct);
      progress.sectors[domain] = sector;
    }

    // Track processed session
    if (session.id) {
      if (!progress.processedSessionIds) progress.processedSessionIds = [];
      if (!progress.processedSessionIds.includes(session.id)) {
        progress.processedSessionIds.push(session.id);
      }
    }
  }

  // Apply decay from last session to today
  const lastSessionDate = operationSessions.length > 0
    ? new Date(operationSessions[operationSessions.length - 1].date).toISOString().split('T')[0]
    : progress.lastDecayDate;
  const finalDays = daysBetween(lastSessionDate, today);
  const finalSanctioned = countSanctionedRestDaysInRange(
    progress.sanctionedRestDays, lastSessionDate, today
  );
  progress = applyDecay(progress, today, Math.max(0, finalDays - finalSanctioned), undefined, undefined);

  // Check victory conditions
  const victory = checkVictoryConditions(progress);
  progress.victoryConditions = {
    allSectorsAbove80: victory.allSectorsAbove80,
    consistencyAbove70: victory.consistencyAbove70,
    noLargeGaps: victory.noLargeGaps,
  };

  await saveSpearheadProgress(progress);

  // Update leaderboard after reprocessing
  try {
    await updateLeaderboard(userId, progress);
  } catch (e) {
    console.warn('Leaderboard update after reprocess failed:', e);
  }

  const finalControl = {
    alpha: progress.sectors.alpha.controlPct,
    bravo: progress.sectors.bravo.controlPct,
    charlie: progress.sectors.charlie.controlPct,
    delta: progress.sectors.delta.controlPct,
  };

  console.log(`[Spearhead] Reprocessed ${operationSessions.length} sessions. Final control:`, finalControl);
  return { sessionsProcessed: operationSessions.length, finalControl };
}

// ============================================================================
// SHARED UTILITIES
// ============================================================================

/**
 * Get the color for a domain control percentage.
 * Shared across SpearheadView, SpearheadMap, and SpearheadDashboardCard.
 */
export function getDomainColor(pct: number): string {
  if (pct >= 80) return '#22c55e';
  if (pct >= 60) return '#84cc16';
  if (pct >= 40) return '#f59e0b';
  if (pct >= 20) return '#f97316';
  return '#ef4444';
}

/**
 * Get Tailwind bar color class for a domain control percentage.
 */
export function getDomainBarClass(pct: number): string {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Get domain status text for a control percentage.
 */
export function getDomainStatus(pct: number): string {
  if (pct >= 80) return 'SECURED';
  if (pct >= 40) return 'CONTESTED';
  return 'THREATENED';
}

/**
 * Check if Operation Spearhead is currently active.
 *
 * SPEARHEAD_ENABLED feature flag — single source of truth for in-app
 * Spearhead visibility. Flipped to true in v1.9.18 — the ARG ramp-up
 * is no longer planned; Spearhead ships as a normal in-app feature.
 *
 * When false, all Spearhead UI is hidden, the dashboard card returns null,
 * session processing skips territory updates, and the operations page redirects
 * to the ARG breadcrumb. All code is preserved — no deletion.
 */
export const SPEARHEAD_ENABLED = true;

export function isSpearheadActive(): boolean {
  if (!SPEARHEAD_ENABLED) return false;
  const today = getLocalDateString();
  return today >= OPERATION_START && today <= OPERATION_END;
}

/**
 * Get days remaining in the operation.
 */
export function getSpearheadDaysRemaining(): number {
  const end = new Date(OPERATION_END + 'T23:59:59');
  const now = new Date();
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}
